import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import {
    getAllRequest, updateAllRequest, resetUpdateRes, getRolesRequest,
    createRoleRequest, resetCreateRoleRes,
    deleteRoleRequest, resetDeleteRoleRes,
} from '../../../../redux/modules/permissions/slice';
import { toast } from 'react-toastify';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    ToggleButton,
    ToggleButtonGroup,
    Button,
    CircularProgress,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import BlockIcon from '@mui/icons-material/Block';
import { useNavigate } from 'react-router-dom';

// Fallback labels used only until the server catalog (the single source of
// truth) loads. The live grid columns/rows come from state.permissions.catalog.
const MODULE_LABELS = {
    dashboard: 'Dashboard',
    patients: 'Patients',
    patientAccounts: 'Patient Accounts',
    appointments: 'Appointments',
    labOrders: 'Lab Orders',
    labResults: 'Lab Results',
    labReports: 'Lab Reports',
    labVoucherSubmission: 'Voucher Submission',
    labQC: 'Quality Control',
    labSettings: 'Lab Settings',
    labAnalytics: 'Lab Analytics',
    imaging: 'Imaging',
    analytics: 'General Analytics',
    users: 'User Management',
    externalAccess: 'External Access',
    referrals: 'Referrals',
    settings: 'System Settings',
    billing: 'Billing',
    auditTrail: 'Audit Trail',
};

const MODULE_KEYS = Object.keys(MODULE_LABELS);

// Which hospital module a capability needs is now declared on the capability
// catalog itself (the `requires` field served by the API) — no second map to
// drift. Capabilities with no `requires` are cross-cutting and always shown.
const isHospitalModuleEnabled = (modules, id) => {
    const m = modules?.[id];
    return typeof m === 'boolean' ? m : !!m?.enabled;
};

const PROTECTED_ROLES = ['admin', 'patient', 'externalReferrer'];

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    '& .MuiToggleButton-root': {
        padding: '2px 10px',
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'none',
        border: `1px solid ${theme.palette.divider}`,
        '&.Mui-selected': {
            color: '#fff',
        },
        '&.Mui-selected.read': {
            backgroundColor: theme.palette.info.main,
            '&:hover': { backgroundColor: theme.palette.info.dark },
        },
        '&.Mui-selected.write': {
            backgroundColor: theme.palette.success.main,
            '&:hover': { backgroundColor: theme.palette.success.dark },
        },
    },
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '0.8rem',
    textAlign: 'center',
    padding: '12px 8px',
    backgroundColor: theme.palette.grey[50],
    borderBottom: `2px solid ${theme.palette.divider}`,
    minWidth: 120,
}));

const ModuleCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    fontSize: '0.85rem',
    padding: '10px 16px',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap',
}));

const PermCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'center',
    padding: '6px 8px',
    borderRight: `1px solid ${theme.palette.grey[100]}`,
}));

export default function PermissionSettings() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        allPermissions, modules, catalog, loading, updateLoading, updateRes, roles,
        createRoleLoading, createRoleRes, deleteRoleLoading, deleteRoleRes,
    } = useSelector(state => state.permissions);

    // The grid is driven by the server capability catalog (single source of
    // truth); fall back to the static labels only until it loads.
    const moduleLabels = (Array.isArray(catalog) && catalog.length)
        ? Object.fromEntries(catalog.map(c => [c.module, c.label]))
        : MODULE_LABELS;
    const moduleKeys = (Array.isArray(catalog) && catalog.length)
        ? catalog.map(c => c.module)
        : MODULE_KEYS;
    // Per-capability granularity + hospital-module gating come straight from the
    // server catalog (single source of truth), not a local map.
    const moduleActions = (key) => {
        const entry = Array.isArray(catalog) ? catalog.find(c => c.module === key) : null;
        return entry?.actions || ['read', 'write'];
    };
    const moduleRequires = (key) => {
        const entry = Array.isArray(catalog) ? catalog.find(c => c.module === key) : null;
        return entry?.requires;
    };
    const currentUserRole = useSelector(state => state.auth.userData?.role);
    const hospitalModules = useSelector(state => state.hospitalConfig?.modules);
    const configLoaded = useSelector(state => state.hospitalConfig?.loaded);
    const [localPerms, setLocalPerms] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [newDisplayText, setNewDisplayText] = useState('');

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);

    const [bulkMenu, setBulkMenu] = useState({ anchorEl: null, role: null });

    const CONFIGURABLE_ROLES = (Array.isArray(roles) ? roles : []).filter(r => r.role !== 'externalReferrer');

    const isColumnLocked = (role) => role === 'admin';

    const isAdmin = currentUserRole === 'admin';

    // Rows shown in the grid: only modules relevant to the hospital's enabled
    // modules. Until config loads, show everything (avoid hiding on first paint).
    // Saving still iterates the full MODULE_KEYS so hidden modules keep their
    // stored permissions.
    const visibleModuleKeys = configLoaded
        ? moduleKeys.filter((key) => {
            const gate = moduleRequires(key);
            return !gate || isHospitalModuleEnabled(hospitalModules, gate);
        })
        : moduleKeys;

    useEffect(() => {
        dispatch(setMainHeading("Permissions"));
        dispatch(getAllRequest());
        dispatch(getRolesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (allPermissions.length > 0) {
            const permMap = {};
            for (const doc of allPermissions) {
                const rolePerm = {};
                for (const p of doc.permissions) {
                    rolePerm[p.module] = { enabled: p.enabled, access: p.access };
                }
                permMap[doc.role] = rolePerm;
            }
            setLocalPerms(permMap);
            setHasChanges(false);
        }
    }, [allPermissions]);

    useEffect(() => {
        if (updateRes) {
            if (updateRes.success) {
                toast.success('Permissions saved successfully');
                dispatch(getAllRequest());
            } else {
                toast.error(updateRes.message || 'Failed to save permissions');
            }
            dispatch(resetUpdateRes());
        }
    }, [updateRes, dispatch]);

    useEffect(() => {
        if (createRoleRes) {
            if (createRoleRes.success) {
                toast.success('Role created successfully');
                setAddDialogOpen(false);
                setNewRoleName('');
                setNewDisplayText('');
                dispatch(getRolesRequest());
                dispatch(getAllRequest());
            } else {
                toast.error(createRoleRes.message || 'Failed to create role');
            }
            dispatch(resetCreateRoleRes());
        }
    }, [createRoleRes, dispatch]);

    useEffect(() => {
        if (deleteRoleRes) {
            if (deleteRoleRes.success) {
                toast.success('Role deleted successfully');
                setDeleteDialogOpen(false);
                setRoleToDelete(null);
                dispatch(getRolesRequest());
                dispatch(getAllRequest());
            } else {
                toast.error(deleteRoleRes.message || 'Failed to delete role');
            }
            dispatch(resetDeleteRoleRes());
        }
    }, [deleteRoleRes, dispatch]);

    const getPermission = useCallback((role, moduleKey) => {
        return localPerms[role]?.[moduleKey] || { enabled: false, access: 'read' };
    }, [localPerms]);

    const handleToggleEnabled = (role, moduleKey) => {
        if (isColumnLocked(role)) return;
        const actions = moduleActions(moduleKey);
        setLocalPerms(prev => {
            const current = prev[role]?.[moduleKey] || { enabled: false, access: 'read' };
            // Single-action capabilities (read-only / write-only) have no choice —
            // pin access to their one action so the stored grid stays coherent.
            const access = actions.length === 1 ? actions[0] : current.access;
            return {
                ...prev,
                [role]: {
                    ...prev[role],
                    [moduleKey]: { ...current, enabled: !current.enabled, access },
                },
            };
        });
        setHasChanges(true);
    };

    // One-click grant/revoke for an entire role. Operates on the visible
    // (config-relevant) modules only, so hidden modules keep their stored value.
    const applyBulkToRole = (role, mode) => {
        if (isColumnLocked(role)) return;
        setLocalPerms(prev => {
            const rolePerms = { ...(prev[role] || {}) };
            for (const key of visibleModuleKeys) {
                const current = rolePerms[key] || { enabled: false, access: 'read' };
                if (mode === 'none') {
                    rolePerms[key] = { ...current, enabled: false };
                } else {
                    // Respect each capability's granularity: a write-only module
                    // can't be "read", a read-only module can't be "write".
                    const actions = moduleActions(key);
                    const access = actions.includes(mode) ? mode : actions[0];
                    rolePerms[key] = { enabled: true, access };
                }
            }
            return { ...prev, [role]: rolePerms };
        });
        setHasChanges(true);
    };

    const openBulkMenu = (event, role) => setBulkMenu({ anchorEl: event.currentTarget, role });
    const closeBulkMenu = () => setBulkMenu({ anchorEl: null, role: null });
    const runBulk = (mode) => {
        if (bulkMenu.role) applyBulkToRole(bulkMenu.role, mode);
        closeBulkMenu();
    };

    const handleChangeAccess = (role, moduleKey, newAccess) => {
        if (isColumnLocked(role) || !newAccess) return;
        setLocalPerms(prev => {
            const current = prev[role]?.[moduleKey] || { enabled: false, access: 'read' };
            return {
                ...prev,
                [role]: {
                    ...prev[role],
                    [moduleKey]: { ...current, access: newAccess },
                },
            };
        });
        setHasChanges(true);
    };

    const handleSave = () => {
        const payload = [];
        for (const roleObj of CONFIGURABLE_ROLES) {
            if (roleObj.role === 'admin') continue;
            const rolePerms = localPerms[roleObj.role] || {};
            const permissions = moduleKeys.map(mod => ({
                module: mod,
                enabled: rolePerms[mod]?.enabled || false,
                access: rolePerms[mod]?.access || 'read',
            }));
            payload.push({ role: roleObj.role, permissions });
        }
        dispatch(updateAllRequest(payload));
    };

    const handleReset = () => {
        dispatch(getAllRequest());
    };

    const handleCreateRole = () => {
        if (!newRoleName.trim() || !newDisplayText.trim()) return;
        dispatch(createRoleRequest({ roleName: newRoleName.trim(), displayText: newDisplayText.trim() }));
    };

    const handleDeleteRole = () => {
        if (!roleToDelete) return;
        dispatch(deleteRoleRequest({ role: roleToDelete }));
    };

    const openDeleteDialog = (role) => {
        setRoleToDelete(role);
        setDeleteDialogOpen(true);
    };

    const basePath = `/${useSelector(state => state.auth.userData?.role) || 'admin'}`;

    if (loading && allPermissions.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <IconButton
                    onClick={() => navigate(`${basePath}/settings`)}
                    size="small"
                    sx={{ mr: 1, color: 'text.secondary' }}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
                    Role Permissions
                </Typography>
                {isAdmin && (
                    <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => setAddDialogOpen(true)}
                        sx={{ borderRadius: 2 }}
                        size="small"
                    >
                        Add Role
                    </Button>
                )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Control what each role can access. Tick to enable a module, then set read or write access —
                or use the ⋮ menu on a role to grant or revoke every module at once. Only modules enabled for
                this hospital are shown. Admin always has full access.
            </Typography>

            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 3, mb: 3, overflowX: 'auto' }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <HeaderCell sx={{ textAlign: 'left', minWidth: 160, position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'grey.50' }}>
                                Module
                            </HeaderCell>
                            {CONFIGURABLE_ROLES.map(roleObj => {
                                const locked = isColumnLocked(roleObj.role);
                                const canDelete = isAdmin && !PROTECTED_ROLES.includes(roleObj.role);
                                return (
                                    <HeaderCell key={roleObj.role} colSpan={1}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                                                    {roleObj.displayText}
                                                </Typography>
                                                {isAdmin && !locked && (
                                                    <Tooltip title="Set all modules for this role">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => openBulkMenu(e, roleObj.role)}
                                                            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                                                        >
                                                            <MoreVertIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete role">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openDeleteDialog(roleObj.role)}
                                                            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                                                        >
                                                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            {locked && (
                                                <Chip
                                                    icon={<LockOutlinedIcon sx={{ fontSize: 12 }} />}
                                                    label="Full Access"
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                                />
                                            )}
                                        </Box>
                                    </HeaderCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleModuleKeys.map((moduleKey, idx) => (
                            <TableRow
                                key={moduleKey}
                                sx={{
                                    backgroundColor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                                    '&:hover': { backgroundColor: 'action.hover' },
                                }}
                            >
                                <ModuleCell sx={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: idx % 2 === 0 ? 'background.paper' : 'grey.50' }}>
                                    {moduleLabels[moduleKey] || moduleKey}
                                </ModuleCell>
                                {CONFIGURABLE_ROLES.map(roleObj => {
                                    const locked = isColumnLocked(roleObj.role);
                                    const perm = roleObj.role === 'admin'
                                        ? { enabled: true, access: 'write' }
                                        : getPermission(roleObj.role, moduleKey);
                                    const tooltipText = locked
                                        ? 'Admin always has access'
                                        : (perm.enabled ? 'Disable' : 'Enable');
                                    // Per-capability granularity from the catalog: show the
                                    // Read/Write toggle ONLY when the capability supports both.
                                    // Read-only (e.g. Audit Trail) and write-only (e.g. Authorize
                                    // lab results) capabilities are a single on/off grant.
                                    const actions = moduleActions(moduleKey);
                                    const hasReadWriteChoice = actions.includes('read') && actions.includes('write');
                                    const soleAction = actions.length === 1 ? actions[0] : null;

                                    return (
                                        <PermCell key={roleObj.role}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                <Tooltip title={tooltipText}>
                                                    <span>
                                                        <Checkbox
                                                            checked={perm.enabled}
                                                            onChange={() => handleToggleEnabled(roleObj.role, moduleKey)}
                                                            disabled={locked}
                                                            size="small"
                                                            sx={{
                                                                p: 0.5,
                                                                '&.Mui-checked': { color: 'primary.main' },
                                                            }}
                                                        />
                                                    </span>
                                                </Tooltip>
                                                {hasReadWriteChoice ? (
                                                    <StyledToggleButtonGroup
                                                        value={perm.access}
                                                        exclusive
                                                        onChange={(e, val) => handleChangeAccess(roleObj.role, moduleKey, val)}
                                                        disabled={locked || !perm.enabled}
                                                        size="small"
                                                    >
                                                        <ToggleButton
                                                            value="read"
                                                            className={perm.access === 'read' ? 'read' : ''}
                                                        >
                                                            Read
                                                        </ToggleButton>
                                                        <ToggleButton
                                                            value="write"
                                                            className={perm.access === 'write' ? 'write' : ''}
                                                        >
                                                            Write
                                                        </ToggleButton>
                                                    </StyledToggleButtonGroup>
                                                ) : (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: perm.enabled ? (soleAction === 'write' ? 'success.main' : 'info.main') : 'text.disabled', fontWeight: 600, fontSize: '0.62rem' }}
                                                    >
                                                        {soleAction === 'write' ? 'Write only' : 'Read only'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </PermCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                    variant="outlined"
                    startIcon={<RestoreOutlinedIcon />}
                    onClick={handleReset}
                    disabled={!hasChanges || updateLoading}
                    sx={{ borderRadius: 2 }}
                >
                    Discard Changes
                </Button>
                <Button
                    variant="contained"
                    startIcon={updateLoading ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
                    onClick={handleSave}
                    disabled={!hasChanges || updateLoading}
                    sx={{ borderRadius: 2, px: 4 }}
                >
                    {updateLoading ? 'Saving...' : 'Save Permissions'}
                </Button>
            </Box>

            {/* Per-role bulk grant/revoke menu */}
            <Menu anchorEl={bulkMenu.anchorEl} open={Boolean(bulkMenu.anchorEl)} onClose={closeBulkMenu}>
                <MenuItem onClick={() => runBulk('write')}>
                    <ListItemIcon><DoneAllIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Enable all — Write</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => runBulk('read')}>
                    <ListItemIcon><VisibilityOutlinedIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Enable all — Read only</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => runBulk('none')}>
                    <ListItemIcon><BlockIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Disable all</ListItemText>
                </MenuItem>
            </Menu>

            {/* Add Role Dialog */}
            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Add New Role</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        label="Display Name"
                        placeholder="e.g. Lab Technician"
                        fullWidth
                        value={newDisplayText}
                        onChange={(e) => {
                            setNewDisplayText(e.target.value);
                            const camel = e.target.value
                                .trim()
                                .split(/\s+/)
                                .map((w, i) => i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                                .join('');
                            setNewRoleName(camel);
                        }}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <TextField
                        label="Role Key"
                        placeholder="e.g. labTechnician"
                        fullWidth
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                        helperText="Internal identifier (auto-generated, camelCase)"
                        size="small"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setAddDialogOpen(false)} disabled={createRoleLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateRole}
                        disabled={!newRoleName.trim() || !newDisplayText.trim() || createRoleLoading}
                        sx={{ borderRadius: 2 }}
                    >
                        {createRoleLoading ? <CircularProgress size={20} color="inherit" /> : 'Create Role'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Role Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Delete Role</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the role <strong>{roleToDelete}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This will fail if any users are still assigned to this role. Reassign them first.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteRoleLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteRole}
                        disabled={deleteRoleLoading}
                        sx={{ borderRadius: 2 }}
                    >
                        {deleteRoleLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
